import { Alert, AlertIcon, Box, Flex, Link, Text, useToast } from "@chakra-ui/react";
import { useCallback, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { urls } from "../../../../../urls";
import { LastChangedTag } from "../../../../../common/editing/LastChangedTag/LastChangedTag";
import { useEntityState, useRefusedField } from "../../../../../common/editing/save-coordinator";
import {
  NoticeRoute,
  PublicationStatus,
  Regulation,
  useGetExclusionGroundsTaxonomyQuery,
  useGetSourcingEventQuery,
  usePublishDpsCompetitionMutation,
  usePublishQualificationSchemeCompetitionMutation,
  useOpenNegotiationWithoutNoticeMutation,
  useSubmitDoffinNoticeMutation,
  type FieldErrorDto,
  type OrgDto,
  type PersonDto,
  type SourcingEventDto,
} from "../../../../../autogen/bff-api";
import { inheritsScopeFromScheme, isDpsCompetition } from "../utils/procedure";
import { displayDate } from "../../../../../common/formatting/displayDate";
import { useApiError } from "../../../../../common/errors/useApiError";
import { StepNavigation } from "../../../../../common/StepNavigation";
import { DirectAwardModal } from "../components/DirectAwardModal";
import { isWithoutCompetition } from "../utils/noCompetition";
import type { PublicEventStep, WizardStep } from "../wizardSteps";
import { useReadinessByField } from "../hooks/useReadinessByField";
import { AwardCriteriaSection } from "../components/summary/AwardCriteriaSection";
import { ClassificationSection } from "../components/summary/ClassificationSection";
import { ContractingAuthoritySection } from "../components/summary/ContractingAuthoritySection";
import { DeadlinesAndMilestonesSection } from "../components/summary/DeadlinesAndMilestonesSection";
import { DocumentsSection } from "../components/summary/DocumentsSection";
import { MinimumRequirementsSection } from "../components/summary/MinimumRequirementsSection";
import { OverviewSection } from "../components/summary/OverviewSection";
import { ProcedureSection } from "../components/summary/ProcedureSection";
import { QualificationSection } from "../components/summary/QualificationSection";
import { computeExclusionGroundsSummary } from "../utils/computeExclusionGroundsSummary";
import { EspdPreviewSection } from "../components/espd/EspdPreviewSection";
import { DevFastForwardMenu } from "../../../../sourcing-event/DevFastForwardMenu";
import { DoffinNoAnswerAlert } from "../../../../sourcing-event/result-notice/DoffinNoAnswerAlert";
import { DoffinRefusalAlert, doffinRefusal } from "../../../../sourcing-event/result-notice/DoffinRefusalAlert";
import { type DoffinNoAnswer, doffinNoAnswer } from "../../../../sourcing-event/result-notice/doffinNoAnswer";
import { OpenSchemeCompetitionModal } from "../components/qualificationScheme/OpenSchemeCompetitionModal";
import { OpenWithoutNoticeModal } from "../components/OpenWithoutNoticeModal";
import { isNegotiationWithoutNotice } from "../utils/negotiationWithoutNotice";

interface Props {
  eventId: string;
  organization: OrgDto;
  currentEditor: PersonDto;
  responsibleBuyer: PersonDto;
  responsibleEditControl?: ReactNode;
  previousStep?: () => void;
  navigateToStep: (step: PublicEventStep) => void;
  steps: ReadonlyArray<WizardStep>;
}

export const SummaryStep = ({
  eventId,
  organization,
  currentEditor,
  responsibleBuyer,
  responsibleEditControl,
  previousStep,
  navigateToStep,
  steps,
}: Props) => {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const event = useEntityState<SourcingEventDto>(eventId);
  const refusedTitle = useRefusedField<SourcingEventDto>(eventId, "eventData.title");
  const { readinessFor, isPublishReady, isLoading: readinessLoading, isError: readinessError } =
    useReadinessByField(eventId);
  const { data: persistedEvent } = useGetSourcingEventQuery({ eventId });
  const publishOperation = persistedEvent?.operations?.find((operation) => operation.operation === "PublishSourcingEvent");
  const publishReason = publishOperation?.reason ? t(publishOperation.reason.code) : undefined;
  const titleNotSavedReason = t("The latest change to the title was not saved. Correct the title before you go on.");
  const { data: taxonomy } = useGetExclusionGroundsTaxonomyQuery();
  const [submitDoffin, { isLoading: isSubmittingNotice, isSuccess: hasPublished }] = useSubmitDoffinNoticeMutation();
  const [publishDpsCompetition, { isLoading: isOpeningCompetition, isSuccess: hasOpenedCompetition }] =
    usePublishDpsCompetitionMutation();
  const [
    publishSchemeCompetition,
    { isLoading: isOpeningSchemeCompetition, isSuccess: hasOpenedSchemeCompetition },
  ] = usePublishQualificationSchemeCompetitionMutation();
  const [openWithoutNotice, { isLoading: isOpeningWithoutNotice, isSuccess: hasOpenedWithoutNotice }] =
    useOpenNegotiationWithoutNoticeMutation();
  const isPublishing =
    isSubmittingNotice || isOpeningCompetition || isOpeningSchemeCompetition || isOpeningWithoutNotice;
  const apiError = useApiError();

  const eventPublicContext = event?.eventContext.type === "Public" ? event.eventContext : null;
  const hasPublishedPersisted = useMemo(
    () =>
      (eventPublicContext?.publications ?? []).some(
        (p) => p.channel === "DOFFIN" && p.status !== "FAILED",
      ),
    [eventPublicContext?.publications],
  );
  const underQualificationScheme = eventPublicContext?.qualificationSchemeCompetition != null;
  const withoutNotice = isNegotiationWithoutNotice(eventPublicContext?.procedureType);
  const isPublished =
    hasPublished ||
    hasOpenedCompetition ||
    hasOpenedSchemeCompetition ||
    hasOpenedWithoutNotice ||
    (isDpsCompetition(eventPublicContext?.procedureType) ? eventPublicContext?.dpsCompetition?.invitedAt != null
    : underQualificationScheme || withoutNotice ? persistedEvent?.status != null && persistedEvent.status !== "DRAFT"
    : hasPublishedPersisted);

  // FOA § 26-7: opening the purchase freezes its invited set instead of going to Doffin.
  const isCompetitionUnderScheme = inheritsScopeFromScheme(eventPublicContext);
  const systemHasNoQualifiedSuppliers =
    readinessFor("DpsCompetitionHasMembers")?.satisfied === false ||
    readinessFor("QualificationSchemeCompetitionHasInvitees")?.satisfied === false;
  const systemIsClosed =
    readinessFor("DpsSchemeOpen")?.satisfied === false || readinessFor("QualificationSchemeOpen")?.satisfied === false;
  const schemeId = eventPublicContext?.governingSchemeId ?? undefined;
  const noQualifiedSupplierReason = underQualificationScheme
    ? t("None of the suppliers chosen for this competition is still admitted to the system (forsyningsforskriften § 13-3).")
    : t("At least one supplier must be qualified before a competition can start (FOA § 26-7).");
  const systemClosedReason = underQualificationScheme
    ? t("The qualification system is closed, so no new competition can be opened under it.")
    : t("The dynamic purchasing system is closed, so no new purchase can be opened under it.");
  const firstIntakeHold = readinessFor("DpsFirstIntakeComplete");
  const firstIntakeClosesAt = firstIntakeHold?.satisfied === false ? firstIntakeHold.error?.params?.date : undefined;
  const firstIntakeUnreviewed =
    firstIntakeHold?.satisfied === false ? Number(firstIntakeHold.error?.params?.count ?? 0) : 0;
  const firstIntakeReason = firstIntakeClosesAt
    ? t(
        "This is the first competition under the system. It can be opened once the deadline for the first intake has passed, {{date}} (FOA § 26-7 (2)).",
        { date: displayDate(firstIntakeClosesAt) },
      )
    : firstIntakeUnreviewed > 0
    ? t(
        "{{count}} applications received by the first-intake deadline still wait for assessment. The first competition can be opened once they are assessed (FOA § 26-7 (2)).",
        { count: firstIntakeUnreviewed },
      )
    : null;

  const withoutCompetition = isWithoutCompetition(eventPublicContext?.procedureType);
  const [awardOpen, setAwardOpen] = useState(false);

  const [doffinRefusalBreaches, setDoffinRefusalBreaches] = useState<FieldErrorDto[] | null>(null);
  const [noAnswer, setNoAnswer] = useState<DoffinNoAnswer | null>(null);
  const [confirmingOpen, setConfirmingOpen] = useState(false);
  const mayAlreadyBeSent = noAnswer === "whileSending";

  const handlePublish = useCallback(async () => {
    setDoffinRefusalBreaches(null);
    setNoAnswer(null);
    try {
      if (withoutNotice) {
        await openWithoutNotice({ eventId }).unwrap();
        toast({ title: t("The competition is open for tenders"), status: "success", duration: 6000 });
      } else if (underQualificationScheme) {
        await publishSchemeCompetition({ eventId }).unwrap();
        toast({ title: t("The competition is open for tenders"), status: "success", duration: 6000 });
      } else if (isCompetitionUnderScheme) {
        await publishDpsCompetition({ eventId }).unwrap();
        toast({ title: t("The competition is open for tenders"), status: "success", duration: 6000 });
      } else {
        const submission = await submitDoffin({ sourcingEventId: eventId }).unwrap();
        if (submission.status === PublicationStatus.Published) {
          toast({ title: t("Notice published to Doffin"), status: "success", duration: 6000 });
        }
      }
      navigate(urls.app.sourcingEvents.view.go(eventId), { replace: true });
    } catch (e) {
      const breaches = doffinRefusal(e);
      const unanswered = isCompetitionUnderScheme || withoutNotice ? null : doffinNoAnswer(e);
      if (breaches) setDoffinRefusalBreaches(breaches);
      else if (unanswered) setNoAnswer(unanswered);
      else apiError.trigger(e);
    }
  }, [
    withoutNotice,
    openWithoutNotice,
    underQualificationScheme,
    isCompetitionUnderScheme,
    publishSchemeCompetition,
    publishDpsCompetition,
    submitDoffin,
    eventId,
    toast,
    t,
    apiError,
    navigate,
  ]);

  const runsStep = (step: PublicEventStep) => steps.some((s) => s.key === step);

  const exclusionGroundsSummary = useMemo(
    () => computeExclusionGroundsSummary(taxonomy, eventPublicContext?.activeExclusionGroundIds ?? []),
    [taxonomy, eventPublicContext?.activeExclusionGroundIds],
  );

  if (!event) return null;

  const regulationNotYetSupported = readinessFor("RegulationSupportsPublishing")?.satisfied === false;
  const announcementBlockedBelowEea =
    regulationNotYetSupported && eventPublicContext?.regulation === Regulation.Forsyningsforskriften;

  return (
    <Box pb="8" pt="10">
      {readinessError && (
        <Alert status="error" borderRadius="lg" mb="6">
          <AlertIcon />
          <Text fontSize="sm">
            {t("Could not load publish readiness — refresh the page to retry. Section status indicators below may not be accurate.")}
          </Text>
        </Alert>
      )}
      {regulationNotYetSupported && (
        <Alert status="warning" borderRadius="lg" mb="6">
          <AlertIcon />
          <Text fontSize="sm">
            {announcementBlockedBelowEea
              ? t("Procurements below the EEA threshold have no announcement duty under this regulation, and a voluntary Doffin announcement is not yet supported. The procurement can be registered and made ready, but publishing stays unavailable.")
              : t("Doffin announcements are not yet supported for this regulation. The procurement can be registered and made ready, but publishing stays unavailable until announcement support arrives.")}
          </Text>
        </Alert>
      )}
      {systemIsClosed ? (
        <SchemeBlockerAlert
          reason={systemClosedReason}
          linkText={underQualificationScheme ? t("Open the qualification system") : t("Open the dynamic purchasing system")}
          to={schemeId ? urls.app.sourcingEvents.view.go(schemeId) : undefined}
        />
      ) : firstIntakeReason ? (
        <SchemeBlockerAlert
          reason={firstIntakeReason}
          linkText={firstIntakeClosesAt ? t("Open the dynamic purchasing system") : t("Assess applications")}
          to={
            schemeId
              ? firstIntakeClosesAt
                ? urls.app.sourcingEvents.view.go(schemeId)
                : `${urls.app.sourcingEvents.view.go(schemeId)}?tab=dpsSuppliers`
              : undefined
          }
        />
      ) : systemHasNoQualifiedSuppliers ? (
        <SchemeBlockerAlert
          reason={noQualifiedSupplierReason}
          linkText={t("Assess applications")}
          to={schemeId ? `${urls.app.sourcingEvents.view.go(schemeId)}?tab=dpsSuppliers` : undefined}
        />
      ) : null}
      {!withoutCompetition && (
        <DeadlinesAndMilestonesSection
          event={event}
          readinessFor={readinessFor}
          onWizardStepNavigate={navigateToStep}
        />
      )}
      <OverviewSection
        event={event}
        refusedTitle={
          refusedTitle && { savedTitle: refusedTitle.savedState.eventData.title, error: refusedTitle.error }
        }
        organization={organization}
        readinessFor={readinessFor}
        onWizardStepNavigate={navigateToStep}
      />
      <ClassificationSection event={event} readinessFor={readinessFor} onWizardStepNavigate={navigateToStep} />
      <ProcedureSection event={event} readinessFor={readinessFor} onWizardStepNavigate={navigateToStep} />
      {runsStep("qualification") && (
        <QualificationSection
          event={event}
          readinessFor={readinessFor}
          onWizardStepNavigate={navigateToStep}
          exclusionGroundsSummary={exclusionGroundsSummary}
        />
      )}
      {runsStep("awardCriteria") && (
        <AwardCriteriaSection event={event} readinessFor={readinessFor} onWizardStepNavigate={navigateToStep} />
      )}
      {runsStep("minimumRequirements") && (
        <MinimumRequirementsSection event={event} onWizardStepNavigate={navigateToStep} />
      )}
      <DocumentsSection event={event} fieldStatuses={[]} onWizardStepNavigate={navigateToStep} />
      <ContractingAuthoritySection
        organization={organization}
        currentEditor={currentEditor}
        responsibleBuyer={responsibleBuyer}
        responsibleEditControl={responsibleEditControl}
        buyerLegalType={eventPublicContext?.buyerLegalType}
        readinessFor={readinessFor}
      />
      {!withoutCompetition && event.collectsEspdWithBid && (event.asksForEspd ?? true) && (
        <EspdPreviewSection
          event={event}
          organization={organization}
          exclusionGroundsSummary={exclusionGroundsSummary}
        />
      )}

      <Flex justify="center" mb="6" mt="2" gap="4" align="center">
        <LastChangedTag timestamp={event.modifiedAt} />
        <DevFastForwardMenu
          event={event}
          onForcePublished={() => navigate(urls.app.sourcingEvents.view.go(eventId), { replace: true })}
        />
      </Flex>

      {!isCompetitionUnderScheme && !withoutNotice && event.contractNoticeRoute && (
        <Text textAlign="center" fontSize="sm" color="smMuted" mt="2" mb="-4" data-testid="notice-route">
          {event.contractNoticeRoute === NoticeRoute.Eu
            ? t("EU notice in Doffin, forwarded to TED")
            : t("National notice in Doffin, not sent to the EU (TED)")}
        </Text>
      )}
      {doffinRefusalBreaches && (
        <Box mt="6">
          <DoffinRefusalAlert
            breaches={doffinRefusalBreaches}
            heading={t("Doffin refused the contract notice. Nothing has been published.")}
            advice={t("Correct the procurement and publish again, or contact support.")}
          />
        </Box>
      )}
      {noAnswer && (
        <Box mt="6">
          <DoffinNoAnswerAlert noAnswer={noAnswer} />
        </Box>
      )}
      <StepNavigation
        onPrevious={previousStep}
        onNext={
          withoutCompetition
            ? () => setAwardOpen(true)
            : underQualificationScheme || withoutNotice
            ? () => setConfirmingOpen(true)
            : handlePublish
        }
        nextLabel={
          withoutCompetition
            ? t("Award the contract")
            : isCompetitionUnderScheme || withoutNotice
            ? t("Open for tenders")
            : t("Publish to Doffin")
        }
        nextIsAction
        isNextLoading={isPublishing}
        nextLoadingText={t("Publishing…")}
        isNextDisabled={
          !publishOperation?.allowed ||
          readinessLoading ||
          readinessError ||
          isPublishing ||
          isPublished ||
          mayAlreadyBeSent ||
          !isPublishReady ||
          !!refusedTitle
        }
        nextDisabledReason={
          !publishOperation?.allowed ? publishReason : refusedTitle && !isPublished ? titleNotSavedReason : undefined
        }
        nextTooltip={
          !publishOperation?.allowed
            ? publishOperation?.reason && t(publishOperation.reason.code)
            : refusedTitle && !isPublished
            ? titleNotSavedReason
            : readinessError
            ? t("Could not load readiness status — try refreshing the page")
            : isPublishing
            ? t("Publishing…")
            : mayAlreadyBeSent && !isPublished
            ? t("zz_DoffinApiErrorCode_DoffinDidNotAnswerWhileSending")
            : isPublished
            ? isCompetitionUnderScheme || withoutNotice
              ? t("The competition is already open")
              : t("Already published to Doffin")
            : announcementBlockedBelowEea
            ? t("Doffin announcements are not yet supported below the EEA threshold")
            : regulationNotYetSupported
            ? t("Doffin announcements are not yet supported for this regulation")
            : !isPublishReady && !readinessLoading && systemIsClosed
            ? systemClosedReason
            : !isPublishReady && !readinessLoading && firstIntakeReason
            ? firstIntakeReason
            : !isPublishReady && !readinessLoading && systemHasNoQualifiedSuppliers
            ? noQualifiedSupplierReason
            : !isPublishReady && !readinessLoading && withoutCompetition
            ? t("Fill in all required fields above to award the contract")
            : !isPublishReady && !readinessLoading
            ? t("Fill in all required fields above to enable publishing")
            : null
        }
      />
      {confirmingOpen && withoutNotice && (
        <OpenWithoutNoticeModal
          eventId={eventId}
          onClose={() => setConfirmingOpen(false)}
          onConfirmed={handlePublish}
        />
      )}
      {confirmingOpen && !withoutNotice && (
        <OpenSchemeCompetitionModal
          eventId={eventId}
          onClose={() => setConfirmingOpen(false)}
          onConfirmed={handlePublish}
        />
      )}
      {withoutCompetition && (
        <DirectAwardModal
          eventId={eventId}
          documents={event.eventData.documents ?? []}
          isOpen={awardOpen}
          onClose={() => setAwardOpen(false)}
          onAwarded={() => {
            toast({ title: t("The contract is awarded"), status: "success", duration: 6000 });
            navigate(urls.app.sourcingEvents.view.go(eventId), { replace: true });
          }}
        />
      )}
    </Box>
  );
};

const SchemeBlockerAlert = ({ reason, linkText, to }: { reason: string; linkText: string; to: string | undefined }) => (
  <Alert status="warning" borderRadius="lg" mb="6">
    <AlertIcon />
    <Text fontSize="sm">
      {reason}{" "}
      {to && (
        <Link as={RouterLink} to={to} fontWeight="semibold" textDecoration="underline">
          {linkText}
        </Link>
      )}
    </Text>
  </Alert>
);
